import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  useBulkDeletePackageTemplates,
  useBulkTogglePackageTemplateStatus,
  useDeletePackageTemplate,
  usePackageTemplatesQuery,
} from "../api/queries";
import { PackageTemplatesFilter } from "../ui/PackageTemplatesFilter";
import { PackageTemplatesTable } from "../ui/PackageTemplatesTable";
import { PackageTemplatesToolbar } from "../ui/PackageTemplatesToolbar";

export function PackageTemplatesPage() {
  const nav = useNavigate({ from: "/app/packages/templates" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/packages/templates",
  });

  const list = usePackageTemplatesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["packages:create"]);
  const deleteTemplate = useDeletePackageTemplate();
  const bulkDelete = useBulkDeletePackageTemplates();
  const bulkToggleStatus = useBulkTogglePackageTemplateStatus();

  const handleBulkDelete = async (ids: string[]) => {
    await toast.promise(bulkDelete.run(ids), {
      loading: "ກຳລັງລຶບ...",
      success: (result) =>
        `ລຶບ template ແພັກເກດ ${result.deletedCount} ລາຍການສຳເລັດ`,
      error: "ລຶບ template ແພັກເກດລົ້ມເຫຼວ",
    });
  };

  const handleBulkToggleStatus = async (
    ids: string[],
    status: "active" | "inactive",
  ) => {
    const statusLabel = status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    await toast.promise(bulkToggleStatus.run({ ids, status }), {
      loading: "ກຳລັງປ່ຽນສະຖານະ...",
      success: (result) =>
        `${statusLabel} template ແພັກເກດ ${result.updatedCount} ລາຍການສຳເລັດ`,
      error: "ປ່ຽນສະຖານະ template ແພັກເກດລົ້ມເຫຼວ",
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Template ແພັກເກດ" },
          ]}
        />
      </Header>

      <Main>
        <PackageTemplatesToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/packages/templates/create" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <PackageTemplatesFilter />

          <PackageTemplatesTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onView={(template) =>
              nav({
                to: "/app/packages/templates/$id",
                params: { id: template.id },
              })
            }
            onEdit={(template) =>
              nav({
                to: "/app/packages/templates/$id/edit",
                params: { id: template.id },
              })
            }
            onDelete={async (id) =>
              toast.promise(deleteTemplate.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບ template ແພັກເກດສຳເລັດ",
                error: "ລຶບ template ແພັກເກດລົ້ມເຫຼວ",
              })
            }
            onBulkDelete={handleBulkDelete}
            onBulkToggleStatus={handleBulkToggleStatus}
          />
        </div>
      </Main>
    </>
  );
}
